// Live voice-to-draft dictation, shared by every composer surface (Workspace,
// Mail Merge, Sequences). Uses the browser's built-in speech recognition
// (window.SpeechRecognition / webkitSpeechRecognition — Chrome/Edge only,
// no server-side transcription cost) in `continuous` mode so the mic stays
// open across pauses, and streams each *finalized* chunk of speech to
// POST /ai/voice-draft, which decides whether that chunk is new content to
// weave into the draft or a spoken edit instruction ("make it shorter",
// "start over") to apply, and returns the full updated subject+body.
//
// Deliberately keeps the client dumb: it never tries to diff or patch text
// itself, it just displays whatever the server returns. All the "is this
// dictation or a command" judgment lives in one place (the prompt), so
// behavior is consistent across every composer that adopts this hook.
import { useCallback, useEffect, useRef, useState } from "react";
import { voiceDraft } from "../api";
import { escapeHtml } from "./pasteClean";

export function isVoiceDictationSupported() {
  return typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

// Plain text (blank line = new paragraph) -> the same canonical HTML shape
// Quill itself produces, so setting it back into a controlled <RichTextEditor
// value=.../> round-trips cleanly instead of fighting Quill's own paragraph
// model. Content is escaped — this HTML is being *set as the value*, not
// inserted through Quill's paste pipeline, so nothing else sanitizes it.
export function voiceTextToHtml(text) {
  const paragraphs = String(text || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return "<p><br></p>";
  return paragraphs
    .map((para) => "<p>" + escapeHtml(para).replace(/\n/g, "<br>") + "</p>")
    .join("");
}

/**
 * @param {() => any} getQuill - returns the live Quill instance (for a clean
 *   plain-text snapshot of the current body via quill.getText()) or null.
 * @param {string} subject - current subject line.
 * @param {(v:string)=>void} setSubject
 * @param {(html:string)=>void} setBodyHtml - the composer's own onChange/setter for body HTML.
 * @param {object} sdrSettings - { sdrName, company, valueProp } for the prompt.
 * @param {string} [recipientContext] - optional "replying to X at Y about Z" hint.
 * @param {(msg:string, kind?:string)=>void} [addToast]
 */
export function useVoiceDraft({ getQuill, subject, setSubject, setBodyHtml, sdrSettings, recipientContext = "", addToast = () => {} }) {
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [busy, setBusy] = useState(false); // a voice-draft request is in flight
  const recognitionRef = useRef(null);
  const stoppedByUserRef = useRef(true);
  const inFlightRef = useRef(false);
  const pendingChunkRef = useRef(""); // finalized speech that arrived while a request was in flight
  const subjectRef = useRef(subject);
  useEffect(() => { subjectRef.current = subject; }, [subject]);

  const sendChunk = useCallback(async (chunk) => {
    if (!chunk || !chunk.trim()) return;
    if (inFlightRef.current) {
      // A request is already running — fold this chunk in behind it rather
      // than firing overlapping requests that could apply out of order.
      pendingChunkRef.current = (pendingChunkRef.current ? pendingChunkRef.current + " " : "") + chunk.trim();
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    try {
      const quill = getQuill && getQuill();
      const currentBody = quill ? quill.getText().trim() : "";
      const result = await voiceDraft({
        transcriptChunk: chunk.trim(),
        currentSubject: subjectRef.current || "",
        currentBody,
        settings: sdrSettings || {},
        recipientContext,
      });
      if (result && result.changed !== false) {
        if (typeof result.subject === "string" && setSubject) setSubject(result.subject);
        if (typeof result.body === "string" && setBodyHtml) setBodyHtml(voiceTextToHtml(result.body));
      }
    } catch (e) {
      addToast("Voice draft failed: " + e.message, "error");
    } finally {
      inFlightRef.current = false;
      setBusy(false);
      // Anything that arrived mid-request gets sent now, as its own turn —
      // recurses at most once per queued backlog since sendChunk clears
      // pendingChunkRef before re-entering.
      if (pendingChunkRef.current) {
        const next = pendingChunkRef.current;
        pendingChunkRef.current = "";
        sendChunk(next);
      }
    }
  }, [getQuill, setSubject, setBodyHtml, sdrSettings, recipientContext, addToast]);

  const stop = useCallback(() => {
    stoppedByUserRef.current = true;
    setListening(false);
    setInterimText("");
    try { recognitionRef.current && recognitionRef.current.stop(); } catch (_) {}
  }, []);

  const start = useCallback(() => {
    if (!isVoiceDictationSupported()) {
      addToast("Voice dictation needs Chrome or Edge", "error");
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          sendChunk(res[0].transcript);
        } else {
          interim += res[0].transcript;
        }
      }
      setInterimText(interim);
    };
    recognition.onerror = (event) => {
      // "no-speech" fires constantly during normal pauses in continuous
      // mode — not a real error, onend's auto-restart below handles it.
      if (event.error === "no-speech" || event.error === "aborted") return;
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        addToast("Microphone access was blocked — allow it in the browser's site settings to dictate", "error");
        stoppedByUserRef.current = true;
      } else {
        addToast("Voice recognition error: " + event.error, "error");
      }
    };
    recognition.onend = () => {
      // Chrome silently ends recognition after a period of continuous use
      // even with continuous:true — restart automatically unless the user
      // explicitly stopped, so dictation genuinely feels uninterrupted.
      if (!stoppedByUserRef.current) {
        try { recognition.start(); } catch (_) {}
      }
    };

    stoppedByUserRef.current = false;
    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch (e) {
      addToast("Couldn't start voice dictation: " + e.message, "error");
      setListening(false);
    }
  }, [addToast, sendChunk]);

  const toggleListening = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  useEffect(() => () => { stoppedByUserRef.current = true; try { recognitionRef.current && recognitionRef.current.stop(); } catch (_) {} }, []);

  return { supported: isVoiceDictationSupported(), listening, interimText, busy, toggleListening };
}
